import { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { activeCoursesAtom, isOnlineUserAtom, userAtom } from '../atom/Atom';
import { THEMES, ACCESSIBLE_COURSE_CODES } from '../constants/Consts';
import { Loading } from '../components/Loading';
import { useDisneyAvailability } from '../hooks/api/useDisneyAvailability';
import { TypingUnavailable } from '../pages/TypingUnavailable';

interface PermitGuardRouteProps {
  children: ReactElement;
  themeType?: THEMES;
  fallbackPath?: string;
}

export const PermitGuardRoute = ({ children, themeType, fallbackPath = '/error' }: PermitGuardRouteProps) => {
  const location = useLocation();
  const activeCourses = useAtomValue(activeCoursesAtom);
  const isOnlineUser = useAtomValue(isOnlineUserAtom);
  const user = useAtomValue(userAtom);
  const { availability, retry } = useDisneyAvailability(isOnlineUser && themeType === THEMES.DISNEY);

  if (!user || !activeCourses) {
    return <Loading />;
  }

  if (isOnlineUser) {
    if (themeType === THEMES.DISNEY) {
      if (availability === 'checking') return <Loading />;
      if (availability === 'retired') return <TypingUnavailable />;
      if (availability === 'error') {
        return (
          <main className="min-h-screen flex flex-col items-center justify-center gap-6 text-main_text">
            <p>タイピングを確認できませんでした。</p>
            <button className="rounded-full bg-current_key text-white px-8 py-3" onClick={retry}>もういちど確認する</button>
          </main>
        );
      }
    }
    return children;
  }

  if (themeType && !ACCESSIBLE_COURSE_CODES.LICENSE[themeType]?.some((course) => activeCourses.includes(course))) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children;
};
